/**
 * The deeper search on the case page, as pure rules (round 2, 13 Oct 2026): which
 * proposal belongs to the customer line and to a row, which one is the first choice, and
 * when a person may act on it. No React here, so the rules can be tested without a render.
 *
 * Page rules of the round:
 *   - a proposal with a doubt (`downgraded`, `unverified`) is a candidate: it is never the
 *     first row and never the one-click choice;
 *   - an alternative is shown under the item of a line and changes nothing until a person
 *     picks it;
 *   - the buttons are hidden for a parked case, for a case that is not open and while
 *     I2Q reads the case again;
 *   - a confirmed proposal goes the way of every confirmation ("Confirm" on the customer
 *     line or on the row), with the id of the proposal in the request, so the history
 *     says that the answer came from a proposal.
 */

import { isOutOfScopeCase } from "@/lib/intents";
import {
  type CaseInvestigation,
  type InvestigationProposal,
  type QuoteCaseDetail,
  type QuoteLine,
} from "@/lib/mock";

type Detail = Pick<
  QuoteCaseDetail,
  | "investigation"
  | "work"
  | "customerBlock"
  | "customerCertainty"
  | "workStatus"
  | "state"
  | "intent"
  | "engineIntent"
>;

function sameCode(a: string | null | undefined, b: string | null | undefined): boolean {
  return Boolean(a && b && a.trim().toUpperCase() === b.trim().toUpperCase());
}

/** True while I2Q reads the case again (after a confirmation or a "Look again" request). */
export function isReadingAgain(detail: Pick<QuoteCaseDetail, "state">): boolean {
  return detail.state === "queued" || detail.state === "processing";
}

/**
 * Whether a person may act on a proposal or ask for "Look again": the case is an inquiry
 * in scope (not a parked order), it is open, and I2Q is not reading it at the moment.
 */
export function canActOnProposals(detail: Detail): boolean {
  const open = (detail.workStatus ?? "open") === "open";
  return open && !isOutOfScopeCase(detail) && !isReadingAgain(detail);
}

/** A proposal that holds: no doubt on it, so it may be the first choice. */
export function holds(proposal: Pick<InvestigationProposal, "status">): boolean {
  return proposal.status === "proposed";
}

/** The account the case is settled on: confirmed by a person, else certain by I2Q. */
function settledAccount(detail: Detail): string | null {
  const confirmed = detail.work?.customer_account ?? null;
  if (confirmed) {
    return confirmed;
  }
  return detail.customerCertainty === "certain" ? (detail.customerBlock?.account ?? null) : null;
}

/**
 * The customer proposal to show on the customer line, or null: none while the case is
 * already settled on that same account (there is nothing left to decide).
 */
export function customerProposalOf(detail: Detail): InvestigationProposal | null {
  const proposal = detail.investigation?.customer ?? null;
  if (!proposal?.account || sameCode(proposal.account, settledAccount(detail))) {
    return null;
  }
  return proposal;
}

/**
 * Whether the customer proposal is the first choice of the customer line: it holds,
 * nobody rejected it, and it is the account the line offers first.
 */
export function proposalIsFirstChoice(
  proposal: InvestigationProposal | null,
  firstAccount: string | null | undefined,
): boolean {
  return Boolean(
    proposal &&
    holds(proposal) &&
    proposal.decision !== "rejected" &&
    sameCode(proposal.account, firstAccount),
  );
}

/** The id to send with a customer confirmation: the proposal for this account, if any. */
export function customerProposalIdFor(
  investigation: CaseInvestigation | null | undefined,
  account: string | null,
): string | undefined {
  // The proposal itself, or the second account of an agent pair.
  return [investigation?.customer, investigation?.otherAccount].find(
    (proposal) => proposal && sameCode(proposal.account, account),
  )?.id;
}

/** The proposals of one line, by its key: the pick first, then the alternatives. */
export function lineProposalsOf(
  investigation: CaseInvestigation | null | undefined,
  lineKey: string | null | undefined,
): InvestigationProposal[] {
  if (!investigation || !lineKey) {
    return [];
  }
  return investigation.lines
    .filter((proposal) => proposal.lineKey === lineKey)
    .sort((a, b) => Number(a.role === "alternative") - Number(b.role === "alternative"));
}

/**
 * Whether a line proposal is the item the row shows now (the row's "Confirm" then
 * confirms the proposal). An alternative never is: it stands beside the item.
 */
export function proposalIsRowItem(
  proposal: InvestigationProposal,
  line: Pick<QuoteLine, "matchedItemId">,
): boolean {
  return proposal.role === "pick" && sameCode(proposal.item, line.matchedItemId);
}

/**
 * The id to send with a line decision: the proposal of this line for the item the person
 * confirms ("confirm": the item the row shows) or picks ("item": the item named).
 */
export function lineProposalIdFor(
  investigation: CaseInvestigation | null | undefined,
  line: Pick<QuoteLine, "lineKey" | "matchedItemId">,
  request: { kind: string; item?: string },
): string | undefined {
  if (request.kind !== "confirm" && request.kind !== "item") {
    return undefined;
  }
  const item = request.kind === "confirm" ? line.matchedItemId : request.item;
  return lineProposalsOf(investigation, line.lineKey).find((proposal) =>
    sameCode(proposal.item, item),
  )?.id;
}

/**
 * Whether "Look again" is offered: the server allows it for the case, nothing is
 * pending, and a person may act. For one line also: the line has a key and no decision.
 */
export function canLookAgain(
  detail: Detail,
  line?: Pick<QuoteLine, "lineKey" | "decision" | "override"> | null,
): boolean {
  const investigation = detail.investigation;
  if (!investigation?.lookAgainAllowed || investigation.pending || !canActOnProposals(detail)) {
    return false;
  }
  if (line === undefined || line === null) {
    return true;
  }
  return Boolean(line.lineKey) && !line.decision && !line.override;
}
